import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { DocumentService } from '../../services/document.service';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { ProposalService } from '../../services/proposal.service';
import { PdfComponent } from '../../pdf-viewer/pdf-viewer.component';
import { buildTestId, createTestIdScope } from '../../shared/testid/testid.util';

// Same accept list as the documents-dialog modal this card replaces.
const ACCEPT_LIST =
  '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.tiff,.gif,.html,.zip';

// "Project & review documents" — client-/system-provided, read-only
// (view/download only, no delete).
const RECEIVED_TYPES = ['PROPOSAL', 'SITE', 'ATP', 'SURVEY_CHECKLIST'];

// "Your deliverables" (PA) / survey + supporting documents (ALTA) —
// vendor-uploaded, upload/view/download/delete.
const DELIVERABLE_TYPES = ['DELIVERABLE', 'REVISION'];

// Proposal statuses that gate the upload affordance — same gating the old
// documents-dialog modal applied.
const UPLOAD_ENABLED_STATUSES = ['ACTIVE', 'REVISION_REQUESTED'];

// Type-pill mapping (Spec 2 `.pill`/`.pill--*` primitives), locked in the 5B
// spec: Site/Proposal/Deliverable -> info, Revision -> warning, Award ->
// success. SURVEY_CHECKLIST isn't called out explicitly there; treated as
// info (received/neutral), consistent with the other received types.
interface TypePillConfig {
  modifier: string;
  label: string;
}
const TYPE_PILL_MAP: Record<string, TypePillConfig> = {
  SITE: { modifier: 'pill--info', label: 'Site' },
  PROPOSAL: { modifier: 'pill--info', label: 'Proposal' },
  DELIVERABLE: { modifier: 'pill--info', label: 'Deliverable' },
  REVISION: { modifier: 'pill--warning', label: 'Revision' },
  ATP: { modifier: 'pill--success', label: 'Award' },
  SURVEY_CHECKLIST: { modifier: 'pill--info', label: 'Survey Checklist' },
};
const FALLBACK_PILL: TypePillConfig = { modifier: '', label: 'Document' };

/**
 * CONNECT 2.0 Documents card (Spec 5B) — inline replacement for the
 * `documents-dialog` modal in the 5A workspace Documents slot. Splits the
 * merged site+proposal document list into a read-only "Project & review
 * documents" subsection and an upload-enabled deliverables subsection; the
 * ALTA/survey variant (`isAlta`) relabels the latter "Upload Survey" +
 * "Supporting Documents" + a "Latest Survey Uploaded" summary line (which
 * feeds 5A's Action Items checklist since that reads the same merged
 * document list).
 *
 * Reuses `DocumentService` verbatim (upload/download/delete/PDF-viewer) —
 * no storage/backend changes. `documents-dialog` itself is left untouched;
 * it still backs the pre-award (Spec 4) and project-history/overview screens.
 *
 * Task 3 (multi-product workspace) — the received/deliverable split above is
 * now also how ONE physical site's shared docs (SITE/ATP/PROPOSAL/
 * SURVEY_CHECKLIST — identical across the site's products) get separated from
 * each product's OWN deliverable/revision uploads. `scope` picks which half a
 * given instance renders: the workspace mounts exactly one `scope="shared"`
 * instance (site-level, view-only, sourced from a representative product's
 * documents) plus one `scope="product"` instance per product card (that
 * product's own deliverable/survey documents, upload/delete keyed to its own
 * proposal). A single-product site still sees the same documents it always
 * has — just split across the two instances instead of one merged list.
 */
@Component({
  standalone: true,
  selector: 'app-documents-card',
  imports: [CommonModule, FormsModule, DragDropModule],
  templateUrl: './documents-card.component.html',
  styleUrl: './documents-card.component.scss',
})
export class DocumentsCardComponent {
  // The merged site+proposal document list — the workspace already computes
  // this the same way the old modal did ([...site.documents, ...proposal.documents]).
  @Input() documents: any[] = [];

  // Full siteDetail ({ site, proposal }). Needed (not just the proposal) so
  // an upload/delete response can be re-emitted as a complete updated
  // siteDetail, and for the upload call (`uploadPropsalDocuments` posts the
  // whole proposal alongside the files) + status-based gating/type-inference.
  @Input() siteDetail: any = {};

  // ALTA/survey vs. PA framing (mirrors 5A's single-shell `isAlta` branch).
  // Irrelevant when `scope === 'shared'` (the received section doesn't branch
  // on it), so the site-level instance can omit this input.
  @Input() isAlta: boolean = false;

  // Which half of the received/deliverable split this instance renders:
  //  - 'shared' — the site-level "Project & review documents" list only
  //    (view/download, no upload zone), mounted ONCE per physical site.
  //  - 'product' — that product's own deliverable/survey documents only
  //    (upload/delete keyed to its proposal), mounted once per product card.
  @Input() scope: 'shared' | 'product' = 'product';

  // View-only mode (Spec 9, Task 4) — the /project-history/:id record. Forces
  // the card to view/download only regardless of status: hides the upload zone
  // and every delete affordance.
  @Input() readOnly: boolean = false;

  // Per-instance testid scoping fragment (Spec 13, Task 3 — uniqueness pass).
  // A multi-product workspace mounts one scope="product" instance PER PRODUCT,
  // all sharing the same hard-coded section/field ids below
  // (documents-upload-input, documents-deliverables-section, etc.) — without
  // this, every product card on the same rendered workspace would emit the
  // SAME data-testid, a Playwright strict-mode breaker. Bind this to the
  // workspace's own durable per-product testid (`product.testId`, itself a
  // deduped product slug — see workspace.component.ts buildProduct()), never
  // the product's array index/position. Left unset (empty string) for the
  // single scope="shared" instance, which mounts exactly once per site and
  // needs no scoping — buildTestId silently drops the empty part so its ids
  // are unchanged.
  @Input() testIdBase: string = '';

  // Emits the same `{ siteDetail }` shape the old documents-dialog emitted on
  // `update`, so the workspace's existing `updateDocuments()` handler is
  // reused unchanged.
  @Output() update = new EventEmitter<{ siteDetail: any }>();

  isSubmitting = false;
  pendingUploadDocumentRequests: any[] = [];

  // ALTA "Upload Survey" optional note. Two-way bound to the ALTA-only
  // textarea (`*ngIf="isAlta"`) and sent as the `note` form part on upload
  // (see `upload()` below); non-ALTA uploads never populate it, so they pass
  // it through as empty/undefined and no `note` part is sent.
  surveyNote: string = '';

  // Hard cap on the survey note — matches the decline free-text fields'
  // Salesforce `Decline_Reason__c`-style 255 limit, since this note is
  // expected to flow to Salesforce too. Enforced via the template's
  // `maxlength` (UX-only; connect-service enforces it server-side).
  readonly maxNoteLength = 255;

  constructor(
    private documentService: DocumentService,
    private utilityService: CommonUtilityServiceService,
    private proposalService: ProposalService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  get acceptList(): string {
    return ACCEPT_LIST;
  }

  get proposal(): any {
    return this.siteDetail?.proposal ?? {};
  }

  get receivedDocuments(): any[] {
    return (this.documents ?? []).filter((doc) =>
      RECEIVED_TYPES.includes(doc?.type)
    );
  }

  get deliverableDocuments(): any[] {
    return (this.documents ?? []).filter((doc) =>
      DELIVERABLE_TYPES.includes(doc?.type)
    );
  }

  // Most recently uploaded survey/supporting file, for the ALTA "Latest
  // Survey Uploaded" line — natural array order (same precedent as 5A's
  // `revisedSurveyItem`, which takes the last REVISION-typed doc in-order).
  get latestSurveyDocument(): any {
    const docs = this.deliverableDocuments;
    return docs.length ? docs[docs.length - 1] : null;
  }

  get uploadEnabled(): boolean {
    return (
      this.scope === 'product' &&
      !this.readOnly &&
      UPLOAD_ENABLED_STATUSES.includes(this.proposal.status)
    );
  }

  typePill(type: string): TypePillConfig {
    return TYPE_PILL_MAP[type] ?? FALLBACK_PILL;
  }

  // Delete is hidden for SITE/ATP (client-provided/award docs); the received
  // section never renders a delete affordance at all regardless of this. In
  // read-only mode delete is suppressed for every document type.
  canDelete(doc: any): boolean {
    return !this.readOnly && doc?.type !== 'SITE' && doc?.type !== 'ATP';
  }

  documentDate(doc: any): Date | null {
    const raw =
      doc?.createdDate ?? doc?.uploadedDate ?? doc?.uploadDate ?? doc?.date;
    return raw ? new Date(raw) : null;
  }

  // Scopes a hard-coded field/section id (upload input, submit button, etc.)
  // to this instance via testIdBase. A fresh one-shot scope is fine here —
  // every call is for a genuine singleton within ONE instance (one upload
  // input, one submit button...), so there's nothing to dedupe against; the
  // scope only exists so buildTestId can filter the empty testIdBase part
  // uniformly with the other helpers below.
  private fieldTestId(...parts: string[]): string {
    return buildTestId(createTestIdScope(), 'documents', this.testIdBase, ...parts);
  }

  uploadInputTestId(): string {
    return this.fieldTestId('upload-input');
  }

  surveyNoteTestId(): string {
    return this.fieldTestId('survey-note');
  }

  uploadSubmitTestId(): string {
    return this.fieldTestId('upload-submit');
  }

  latestSurveyTestId(): string {
    return this.fieldTestId('latest-survey');
  }

  deliverablesSectionTestId(): string {
    return this.fieldTestId('deliverables-section');
  }

  surveySectionTestId(): string {
    return this.fieldTestId('survey-section');
  }

  // Shared list-scoped id builder: slugs+dedupes every candidate in `list`
  // via one scope (so two docs/pending-files with the same filename in the
  // SAME list still dedupe to `-2`/`-3`), keyed off whichever candidate
  // matches `item` by identity, then appends `-<control>`. `keyParts` always
  // includes `this.testIdBase` so two product instances rendering the same
  // filename (Task 3's flagged risk — `docTestId`'s dedupe only ever ran
  // within one card's own list) get distinct ids too; buildTestId drops the
  // empty testIdBase part for the single scope="shared" instance.
  private listScopedTestId(
    list: any[],
    item: any,
    keyParts: (candidate: any) => string[],
    control: string
  ): string {
    const scope = createTestIdScope();
    let base = '';
    for (const candidate of list) {
      const id = buildTestId(scope, ...keyParts(candidate));
      if (candidate === item) {
        base = id;
      }
    }
    return `${base || buildTestId(createTestIdScope(), ...keyParts(item))}-${control}`;
  }

  // data-testid for a repeated doc row's action — `document-<testIdBase>-
  // <slug>-<control>` per the Spec 2 convention, slugged from the filename
  // (never the doc's position) and deduped within whichever section
  // (received/deliverables) the doc belongs to AND within this product
  // instance.
  docTestId(doc: any, control: string): string {
    const list = RECEIVED_TYPES.includes(doc?.type)
      ? this.receivedDocuments
      : this.deliverableDocuments;
    return this.listScopedTestId(
      list,
      doc,
      (candidate) => ['document', this.testIdBase, candidate?.fileName ?? 'file'],
      control
    );
  }

  pendingTestId(pending: any, control: string): string {
    return this.listScopedTestId(
      this.pendingUploadDocumentRequests,
      pending,
      (candidate) => [
        'documents',
        this.testIdBase,
        'pending',
        candidate?.fileName ?? 'file',
      ],
      control
    );
  }

  // --- upload flow: drag-drop + picker -> pending list -> upload ---
  onFileSelect(event: any): void {
    if (event?.target?.files?.length > 0) {
      this.processDocumentList(event.target.files);
    }
    // Allow re-selecting the same file after it's removed from the pending list.
    if (event?.target) {
      event.target.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (event?.dataTransfer?.files && event?.dataTransfer?.files?.length > 0) {
      this.processDocumentList(event.dataTransfer.files);
    }
  }

  private processDocumentList(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      this.pendingUploadDocumentRequests.push({
        fileName: files[i].name,
        type: this.utilityService.determineDocumentTypeByProposalStatus(
          this.proposal.status
        ),
        file: files[i],
      });
    }
  }

  removePendingDocument(pending: any): void {
    this.pendingUploadDocumentRequests =
      this.pendingUploadDocumentRequests.filter(
        (doc: any) => doc.file.name !== pending.file.name
      );
  }

  upload(): void {
    if (!(this.pendingUploadDocumentRequests?.length > 0)) {
      return;
    }
    this.isSubmitting = true;
    this.toastr.info('Uploading document(s)...');
    this.documentService
      .uploadPropsalDocuments(
        this.pendingUploadDocumentRequests,
        this.proposal,
        this.surveyNote
      )
      .subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          this.pendingUploadDocumentRequests = [];
          this.surveyNote = '';
          this.processResponse(response);
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  // --- view / download / delete ---
  download(doc: any): void {
    this.toastr.info('Downloading document...');
    this.documentService.downloadDocument(doc);
  }

  openPdfViewer(doc: any): void {
    this.documentService
      .downloadPdfToViewer(doc)
      .then((result) => {
        this.dialog.open(PdfComponent, {
          height: '90%',
          width: '50%',
          panelClass: 'pdf-viewer-dialog-container',
          data: { pdfSrc: result.url, filename: result.filename },
        });
      })
      .catch(() => {
        this.toastr.error('Preview unavailable due to an error.');
      });
  }

  deleteDocument(doc: any): void {
    this.toastr.info('Deleting document...');
    this.documentService
      .deleteProposalDocument(doc.id)
      .subscribe((response: any) => this.processResponse(response));
  }

  private processResponse(response: any): void {
    if (!response || response.status === 'FAILURE') {
      this.toastr.error(
        response?.message || 'An error occurred. Please try again.'
      );
      return;
    }
    const updatedProposal = response.data;
    if (!updatedProposal) {
      this.toastr.error('An error occurred. Please try again.');
      return;
    }
    this.toastr.success(response.message);
    // Preserved from the old modal: a still-SUBMITTED (pre-award) proposal
    // updates the bid list too. Post-award workspace statuses never hit this,
    // but kept for parity/safety since `editBid` is cheap and defensive.
    if (updatedProposal.status === 'SUBMITTED') {
      this.proposalService.editBid(updatedProposal);
    }
    this.update.emit({
      siteDetail: {
        ...this.siteDetail,
        proposal: { ...updatedProposal, updatedOn: new Date() },
      },
    });
  }
}
